import { Controller, useForm, useWatch } from 'react-hook-form';
import { useCountryConfig } from 'hooks/useCountryConfig';
import { editCompanyProfile, getCompanyBySlug } from 'services/company-service';
import { Main as Container } from 'Components/Main';
import { ChipsInput } from 'Components/common/ChipsInput';
import { useQuery } from 'react-query';
import { Shimmer } from 'Components/PageLoader/styles';
import { Dialog, Select, TextField } from 'Components/ui';
import { useSelector, useDispatch } from 'react-redux';
import { useAuthTenant, updateTenant } from 'store/auth';
import { PageHelper } from '../../Components/PageHelper';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PageHeader } from 'Components/ui';

// ─── Editable field whitelist ─────────────────────────────────────────────────

const EDITABLE_FIELDS = [
  'name', 'corporateName', 'description', 'size', 'income',
  'industry', 'domains', 'defaultLanguage', 'timezone',
];

const LATAM_TIMEZONES = [
  { value: 'America/Sao_Paulo',              label: 'Brasília (UTC-3)' },
  { value: 'America/Manaus',                 label: 'Manaus (UTC-4)' },
  { value: 'America/Fortaleza',              label: 'Fortaleza (UTC-3)' },
  { value: 'America/Belem',                  label: 'Belém (UTC-3)' },
  { value: 'America/Santiago',               label: 'Santiago, Chile (UTC-3/-4)' },
  { value: 'America/Bogota',                 label: 'Bogotá, Colômbia (UTC-5)' },
  { value: 'America/Lima',                   label: 'Lima, Peru (UTC-5)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires, Argentina (UTC-3)' },
  { value: 'America/Guayaquil',              label: 'Quito/Guayaquil, Equador (UTC-5)' },
  { value: 'America/Montevideo',             label: 'Montevidéu, Uruguai (UTC-3)' },
  { value: 'America/Caracas',                label: 'Caracas, Venezuela (UTC-4)' },
  { value: 'America/La_Paz',                 label: 'La Paz, Bolívia (UTC-4)' },
  { value: 'America/Asuncion',               label: 'Assunção, Paraguai (UTC-4/-3)' },
  { value: 'America/Mexico_City',            label: 'Cidade do México (UTC-6)' },
  { value: 'UTC',                            label: 'UTC (Universal)' },
];

// ─── SVG helpers ──────────────────────────────────────────────────────────────

const BuildingIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2"/>
    <line x1="4" y1="7" x2="20" y2="7"/>
    <line x1="4" y1="12" x2="20" y2="12"/>
    <line x1="9" y1="12" x2="9" y2="22"/>
    <line x1="15" y1="12" x2="15" y2="22"/>
  </svg>
);

const GlobeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const DocIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14,2 14,8 20,8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const LockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--vo-steel)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;

const SaveBtn = styled.button`
  height: 44px;
  background: var(--color-brand);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 0 24px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: var(--color-brand-strong); }
  &:disabled { background: var(--vo-steel); cursor: not-allowed; }
`;

const CardsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 720px;
`;

// Section cards
const SectionCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const IconBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

const SectionSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

// Fields
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $readonly }) => $readonly ? 'var(--vo-steel)' : 'var(--vo-ink-2)'};

  ${({ $required }) => $required && `
    &::after {
      content: ' *';
      color: var(--vo-danger);
    }
  `}
`;

const ReadonlyWrap = styled.div`
  position: relative;
`;

const FieldHint = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: var(--vo-steel);
  margin: 2px 0 0 0;
`;

const FieldError = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: var(--vo-danger);
  margin: 2px 0 0 0;
`;

const StyledChipsInput = styled(ChipsInput)`
  border: 1.5px solid var(--vo-steel);
  border-radius: 10px;
  min-height: 40px;
  padding: 6px 12px;

  &:focus-within { border-color: var(--color-brand); }

  & > span {
    background: var(--vo-capri-50);
    color: var(--vo-celeste);
    font-family: 'Maven Pro', sans-serif;
    font-size: 12px;
    font-weight: 500;
    border-radius: 20px;
    padding: 3px 10px;
  }
`;

const ShimmerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 720px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const CompanyProfile = () => {
  const { t } = useTranslation(['company']);
  const dispatch = useDispatch();
  const selectedTenant = useSelector(useAuthTenant);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    defaultValues: {
      active: true,
      country: '',
      cnpj: '',
      corporateName: '',
      description: '',
      domains: [],
      income: '',
      industry: '',
      managers: [],
      name: '',
      partner: false,
      roles: [],
      size: '',
      slug: '',
      defaultLanguage: '',
      timezone: '',
    },
  });

  const formmatValues = values => {
    const filtered = {};
    EDITABLE_FIELDS.forEach(field => {
      if (values[field] !== undefined) {
        filtered[field] = values[field] ?? '';
      }
    });
    return filtered;
  };

  const handleComplete = async () => {
    try {
      const formValues = formmatValues(getValues());
      const res = await editCompanyProfile(selectedTenant._id, formValues);

      if (res.status === 200) {
        const updatedTenant = { ...selectedTenant, ...formValues };
        dispatch(updateTenant({ tenant: updatedTenant }));
        Dialog.alert({
          title: t('company:companyProfile.successTitle'),
          message: t('company:companyProfile.successMessage'),
          icon: 'success',
        });
      } else {
        throw new Error('Ocorreu um erro ao tentar editar a Empresa!');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      Dialog.alert({
        title: t('company:companyProfile.errorTitle'),
        message: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const countryValue = useWatch({ control, name: 'country' });
  const { allConfigs } = useCountryConfig(countryValue);
  const countryLabel = allConfigs.find(c => c.countryCode === countryValue)?.name?.pt || countryValue || '';

  const { isLoading, isSuccess, isError } = useQuery(['getCompanyBySlug'], async () => {
    if (selectedTenant.slug === undefined) return null;
    const result = await getCompanyBySlug(selectedTenant.slug);
    if (result?.data) {
      Object.keys(result?.data).forEach(key => setValue(key, result?.data[key]));
    }
    return result?.data;
  });

  const tourSteps = [
    {
      target: 'body',
      content: 'Bem-vindo ao Perfil da Empresa! Aqui você pode editar as informações cadastrais da sua empresa.',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="page-title"]',
      content: 'Aqui está o título da página e o botão para salvar as alterações.',
      placement: 'bottom',
      title: 'Navegação',
    },
    {
      target: '[data-tour="company-info"]',
      content: 'Atualize as informações básicas da empresa como nome fantasia e razão social.',
      placement: 'bottom',
      title: 'Informações da Empresa',
    },
    {
      target: '[data-tour="legal-info"]',
      content: 'Aqui ficam os dados legais da empresa: CNPJ e país.',
      placement: 'bottom',
      title: 'Dados Legais',
    },
    {
      target: '[data-tour="business-info"]',
      content: 'Configure informações sobre o negócio como porte, setor e receita.',
      placement: 'bottom',
      title: 'Dados do Negócio',
    },
    {
      target: '[data-tour="locale-settings"]',
      content: 'Configure o idioma padrão e o fuso horário da empresa.',
      placement: 'bottom',
      title: 'Idioma e Fuso Horário',
    },
    {
      target: '[data-tour="domains"]',
      content: 'Adicione os domínios de e-mail da sua empresa.',
      placement: 'bottom',
      title: 'Domínios',
    },
    {
      target: '[data-tour="description"]',
      content: 'Use este campo para adicionar uma descrição sobre a empresa.',
      placement: 'top',
      title: 'Descrição',
    },
    {
      target: '[data-tour="save-button"]',
      content: 'Após fazer as alterações, clique aqui para salvar.',
      placement: 'bottom',
      title: 'Salvar Alterações',
    },
    {
      target: 'body',
      content: 'Pronto! Agora você sabe como editar o perfil da empresa.',
      placement: 'center',
      title: 'Tour Concluído!',
    },
  ];

  return (
    <Container>
      <PageWrap>
        {isSuccess && (
          <PageHelper
            steps={tourSteps}
            tourKey="company-profile-tour"
            autoStart={true}
            showButton={true}
          />
        )}

        <form onSubmit={handleSubmit(handleComplete)}>

          {/* ── Page Header ──────────────────────────────────────────── */}
          <PageHeader
            data-tour="page-title"
            title={t('company:companyProfile.title')}
            segments={[
              { label: t('common:breadcrumbs.manage') },
              { label: t('company:companyProfile.title') },
            ]}
            actions={
              <SaveBtn type="submit" data-tour="save-button">
                {t('company:companyProfile.save')}
              </SaveBtn>
            }
          />

          {/* ── Loading shimmer ───────────────────────────────────────── */}
          {isLoading && !isError && (
            <ShimmerStack>
              <Shimmer style={{ height: '160px', borderRadius: '14px' }} />
              <Shimmer style={{ height: '300px', borderRadius: '14px' }} />
              <Shimmer style={{ height: '160px', borderRadius: '14px' }} />
              <Shimmer style={{ height: '200px', borderRadius: '14px' }} />
            </ShimmerStack>
          )}

          {/* ── Cards ────────────────────────────────────────────────── */}
          {isSuccess && (
            <CardsCol>

              {/* Card 1 — Informações da empresa */}
              <SectionCard data-tour="company-info">
                <SectionHead>
                  <IconBadge $gradient="linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri))">
                    <BuildingIcon />
                  </IconBadge>
                  <div>
                    <SectionTitle>{t('company:companyProfile.sectionInfo.title')}</SectionTitle>
                    <SectionSub>{t('company:companyProfile.sectionInfo.subtitle')}</SectionSub>
                  </div>
                </SectionHead>
                <FieldGrid>
                  <FieldWrap>
                    <FieldLabel $required>{t('company:companyProfile.name')}</FieldLabel>
                    <TextField
                      fullWidth
                      {...register('name', { required: t('company:companyProfile.nameRequired') })}
                      error={!!errors?.name}
                    />
                    {errors?.name && <FieldError>{errors.name.message}</FieldError>}
                  </FieldWrap>
                  <FieldWrap>
                    <FieldLabel $required>{t('company:companyProfile.corporateName')}</FieldLabel>
                    <TextField
                      fullWidth
                      {...register('corporateName', { required: t('company:companyProfile.corporateNameRequired') })}
                      error={!!errors?.corporateName}
                    />
                    {errors?.corporateName && <FieldError>{errors.corporateName.message}</FieldError>}
                  </FieldWrap>
                  <FieldWrap data-tour="legal-info">
                    <FieldLabel $readonly>{t('company:companyProfile.cnpj')}</FieldLabel>
                    <ReadonlyWrap>
                      <TextField
                        fullWidth
                        {...register('cnpj')}
                        disabled
                        placeholder="–"
                        sx={{ '& .MuiOutlinedInput-input.MuiOutlinedInput-input': { paddingRight: '44px' } }}
                      />
                      <LockIcon />
                    </ReadonlyWrap>
                    <FieldHint>{t('company:companyProfile.cnpjReadonly')}</FieldHint>
                  </FieldWrap>
                  <FieldWrap>
                    <FieldLabel $readonly>{t('company:companyProfile.country')}</FieldLabel>
                    <ReadonlyWrap>
                      <TextField
                        fullWidth
                        value={countryLabel}
                        disabled
                        placeholder="–"
                        sx={{ '& .MuiOutlinedInput-input.MuiOutlinedInput-input': { paddingRight: '44px' } }}
                      />
                      <LockIcon />
                    </ReadonlyWrap>
                    <FieldHint>{t('company:companyProfile.countryReadonly')}</FieldHint>
                  </FieldWrap>
                  <FieldWrap data-tour="business-info">
                    <FieldLabel>{t('company:companyProfile.size')}</FieldLabel>
                    <TextField fullWidth {...register('size')} placeholder={t('company:companyProfile.sizePlaceholder')} />
                  </FieldWrap>
                  <FieldWrap>
                    <FieldLabel>{t('company:companyProfile.industry')}</FieldLabel>
                    <TextField fullWidth {...register('industry')} placeholder={t('company:companyProfile.industryPlaceholder')} />
                  </FieldWrap>
                  <FieldWrap>
                    <FieldLabel>{t('company:companyProfile.income')}</FieldLabel>
                    <TextField fullWidth {...register('income')} placeholder={t('company:companyProfile.incomePlaceholder')} />
                  </FieldWrap>
                </FieldGrid>
              </SectionCard>

              {/* Card 2 — Localização e idioma */}
              <SectionCard data-tour="locale-settings">
                <SectionHead>
                  <IconBadge $gradient="linear-gradient(135deg, var(--vo-success), var(--vo-capri))">
                    <GlobeIcon />
                  </IconBadge>
                  <div>
                    <SectionTitle>{t('company:companyProfile.sectionLocale.title')}</SectionTitle>
                    <SectionSub>{t('company:companyProfile.sectionLocale.subtitle')}</SectionSub>
                  </div>
                </SectionHead>
                <FieldGrid>
                  <FieldWrap>
                    <FieldLabel $required>{t('company:companyProfile.language')}</FieldLabel>
                    <Controller
                      control={control}
                      name="defaultLanguage"
                      rules={{ required: t('company:companyProfile.languageRequired') }}
                      render={({ field: { onChange, value } }) => (
                        <Select
                          name="defaultLanguage"
                          value={value}
                          onChange={onChange}
                          error={!!errors?.defaultLanguage}
                          options={[
                            { value: 'pt', label: t('company:companyProfile.languagePt') },
                            { value: 'en', label: t('company:companyProfile.languageEn') },
                            { value: 'es', label: t('company:companyProfile.languageEs') },
                          ]}
                        />
                      )}
                    />
                    {errors?.defaultLanguage && <FieldError>{errors.defaultLanguage.message}</FieldError>}
                  </FieldWrap>
                  <FieldWrap>
                    <FieldLabel>{t('company:companyProfile.timezone')}</FieldLabel>
                    <Controller
                      control={control}
                      name="timezone"
                      render={({ field: { onChange, value } }) => (
                        <Select
                          name="timezone"
                          value={value}
                          onChange={onChange}
                          options={[
                            { value: '', label: t('company:companyProfile.timezonePlaceholder') },
                            ...LATAM_TIMEZONES,
                          ]}
                        />
                      )}
                    />
                  </FieldWrap>
                </FieldGrid>
              </SectionCard>

              {/* Card 3 — Informações adicionais */}
              <SectionCard>
                <SectionHead>
                  <IconBadge $gradient="linear-gradient(135deg, var(--vo-warning), var(--vo-warning))">
                    <DocIcon />
                  </IconBadge>
                  <div>
                    <SectionTitle>{t('company:companyProfile.sectionAdditional.title')}</SectionTitle>
                    <SectionSub>{t('company:companyProfile.sectionAdditional.subtitle')}</SectionSub>
                  </div>
                </SectionHead>
                <FieldStack>
                  <FieldWrap data-tour="domains">
                    <FieldLabel>{t('company:companyProfile.domains')}</FieldLabel>
                    <Controller
                      control={control}
                      name="domains"
                      render={({ field: { onChange, value } }) => (
                        <StyledChipsInput
                          value={value}
                          onChange={onChange}
                          placeholder={t('company:companyProfile.domainsPlaceholder')}
                        />
                      )}
                    />
                    <FieldHint>{t('company:companyProfile.domainsHint')}</FieldHint>
                  </FieldWrap>
                  <FieldWrap data-tour="description">
                    <FieldLabel>{t('company:companyProfile.description')}</FieldLabel>
                    <TextField
                      multiline
                      fullWidth
                      {...register('description')}
                      rows={3}
                      placeholder={t('company:companyProfile.descriptionPlaceholder')}
                    />
                  </FieldWrap>
                </FieldStack>
              </SectionCard>

            </CardsCol>
          )}
        </form>
      </PageWrap>
    </Container>
  );
};
